import { getSessionStorageUser } from '../utils/sessionStorage';

/**
 * Dueño de una operación encolada (spec 2026-10-01 § 6).
 *
 * Sin esto, quien entra después en una tablet compartida hereda el trabajo del
 * anterior y lo envía con SU token, y a SU tenant. Las librerías que conservan
 * la cola ante un fallo de sesión (Firestore, Replicache) la separan por
 * usuario justo por esto.
 */
export interface QueueOwner {
  userId: string;
  tenantId: string;
}

/** El dueño de la sesión actual, o `undefined` si no hay sesión legible. */
export function currentQueueOwner(): QueueOwner | undefined {
  const user = getSessionStorageUser()?.user;
  if (!user?.id || !user?.activeTenantId) return undefined;
  return { userId: user.id, tenantId: user.activeTenantId };
}

/**
 * Última sesión conocida, en su PROPIA clave de sessionStorage: sobrevive a
 * cerrar sesión (que solo borra `user`) y no arrastra estado entre tests.
 */
// Exportada: `utils/purgeUserStorage.ts` la conserva al cerrar sesión.
export const LAST_OWNER_KEY = 'rois_last_queue_owner';

function rememberOwner(owner: QueueOwner): void {
  try {
    // Solo se escribe si cambia: `ownerForEnqueue` corre en cada encolado.
    const serialized = JSON.stringify(owner);
    if (sessionStorage.getItem(LAST_OWNER_KEY) === serialized) return;
    sessionStorage.setItem(LAST_OWNER_KEY, serialized);
  } catch {
    // Sin sessionStorage no hay memoria: se encola como antes.
  }
}

function lastKnownOwner(): QueueOwner | undefined {
  try {
    const raw = sessionStorage.getItem(LAST_OWNER_KEY);
    const parsed = raw ? (JSON.parse(raw) as QueueOwner) : undefined;
    return parsed?.userId && parsed?.tenantId ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Dueño para sellar lo que se ENCOLA (spec § 12.2): la sesión actual, o si ya
 * no la hay, la última conocida. Lo que termina de guardarse justo después de
 * un logout o de una sesión muerta pertenece a esa sesión, no al siguiente que
 * entre.
 */
export function ownerForEnqueue(): QueueOwner | undefined {
  const current = currentQueueOwner();
  if (current) {
    rememberOwner(current);
    return current;
  }
  return lastKnownOwner();
}

export function sameOwner(a: QueueOwner | undefined, b: QueueOwner | undefined): boolean {
  if (!a || !b) return a === b;
  return a.userId === b.userId && a.tenantId === b.tenantId;
}

/**
 * ¿Se puede enviar esta operación con la sesión actual?
 *
 * - Sin sello (encolada antes de esta versión): sí, como hasta ahora. Es la
 *   única forma de no dejar trabajo atascado al actualizar.
 * - Con sello: solo si coinciden usuario Y tenant. Cambiar de tenant aparca
 *   las del otro, que no deben ir a un tenant distinto.
 */
export function belongsToCurrentSession(
  owner: QueueOwner | undefined,
  current: QueueOwner | undefined,
): boolean {
  if (!owner) return true;
  if (!current) return false;
  return sameOwner(owner, current);
}

/**
 * ¿La sesión de esta operación se fue mientras se enviaba? Logout, entró otro o
 * cambió de tenant (spec 2026-10-12 § 10).
 *
 * Cerrar sesión ya no vacía las colas, así que un envío en curso puede acabar
 * DESPUÉS del logout: el paso que sale sin sesión lo rechaza el servidor
 * («sin autenticar», o «prohibido» en los endpoints con rol), y eso no dice nada
 * de la operación. Leído como siempre pintaba «la sesión ha caducado» al salir a
 * propósito y gastaba un intento, y un «prohibido» se descartaba como veneno.
 * Las colas lo tratan como `SessionChangedError`: pendiente, sin gastar intento
 * ni avisar, y se corta el drenado.
 *
 * Sin sello no se sabe de quién es: no cuenta, la cola la trata como siempre.
 */
export function sessionLeftOperation(owner: QueueOwner | undefined): boolean {
  return !!owner && !belongsToCurrentSession(owner, currentQueueOwner());
}
